import React from 'react';
import { format } from 'date-fns';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const GrantCard = ({ grant, onClick, viewMode = 'grid' }) => {
  const getStatusColor = (status) => {
    const colorMap = {
      open: 'bg-green-100 text-green-800',
      closing_soon: 'bg-yellow-100 text-yellow-800',
      closed: 'bg-red-100 text-red-800',
      review: 'bg-blue-100 text-blue-800',
      awarded: 'bg-purple-100 text-purple-800'
    };
    return colorMap?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      open: 'CircleDot',
      closing_soon: 'Clock',
      closed: 'XCircle',
      review: 'Eye',
      awarded: 'Award'
    };
    return iconMap?.[status] || 'CircleDot';
  };

  const formatAmount = (min, max, currency = 'USD') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    if (min && max && min !== max) {
      return `${formatter?.format(min)} - ${formatter?.format(max)}`;
    }
    if (max) {
      return `Up to ${formatter?.format(max)}`;
    }
    if (min) {
      return `From ${formatter?.format(min)}`;
    }
    return 'Amount TBD';
  };

  const isClosingSoon = () => {
    if (!grant?.deadline) return false;
    const deadline = new Date(grant.deadline);
    const now = new Date();
    const daysUntilDeadline = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    return daysUntilDeadline <= 7 && daysUntilDeadline > 0;
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={onClick}>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-semibold text-foreground truncate">
                {grant?.title}
              </h3>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(grant?.status)}`}>
                <Icon name={getStatusIcon(grant?.status)} size={12} className="mr-1" />
                {grant?.status?.charAt(0)?.toUpperCase() + grant?.status?.slice(1)?.replace('_', ' ')}
              </span>
              {isClosingSoon() && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <Icon name="AlertTriangle" size={12} className="mr-1" />
                  Closing Soon
                </span>
              )}
            </div>

            <p className="text-sm text-muted-foreground mb-2">{grant?.provider_organization}</p>
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {grant?.description}
            </p>

            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="DollarSign" size={14} />
                <span>{formatAmount(grant?.amount_min, grant?.amount_max, grant?.currency)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Calendar" size={14} />
                <span>Due {format(new Date(grant?.deadline), 'MMM dd, yyyy')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Eye" size={14} />
                <span>{grant?.view_count || 0} views</span>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0">
            <Button variant="outline" size="sm">
              <Icon name="ExternalLink" size={14} className="mr-2" />
              View Details
            </Button>
          </div>
        </div>

        {grant?.focus_areas && grant?.focus_areas?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex flex-wrap gap-1">
              {grant?.focus_areas?.slice(0, 3)?.map((area, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary"
                >
                  {area}
                </span>
              ))}
              {grant?.focus_areas?.length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{grant?.focus_areas?.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={onClick}>
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(grant?.status)}`}>
            <Icon name={getStatusIcon(grant?.status)} size={12} className="mr-1" />
            {grant?.status?.charAt(0)?.toUpperCase() + grant?.status?.slice(1)?.replace('_', ' ')}
          </span>
          {isClosingSoon() && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              <Icon name="AlertTriangle" size={12} className="mr-1" />
              Soon
            </span>
          )}
        </div>

        <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
          {grant?.title}
        </h3>

        <p className="text-sm text-muted-foreground mb-3">{grant?.provider_organization}</p>

        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
          {grant?.description}
        </p>

        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="DollarSign" size={14} className="text-green-600" />
            <span className="font-medium text-green-600">
              {formatAmount(grant?.amount_min, grant?.amount_max, grant?.currency)}
            </span>
          </div>

          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>Deadline: {format(new Date(grant?.deadline), 'MMM dd, yyyy')}</span>
          </div>

          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Eye" size={14} />
            <span>{grant?.view_count || 0} views</span>
          </div>
        </div>

        {grant?.focus_areas && grant?.focus_areas?.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {grant?.focus_areas?.slice(0, 2)?.map((area, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary"
                >
                  {area}
                </span>
              ))}
              {grant?.focus_areas?.length > 2 && (
                <span className="text-xs text-muted-foreground flex items-center">
                  +{grant?.focus_areas?.length - 2} more
                </span>
              )}
            </div>
          </div>
        )}

        <Button variant="outline" size="sm" className="w-full">
          <Icon name="ExternalLink" size={14} className="mr-2" />
          View Details
        </Button>
      </div>
    </div>
  );
};

export default GrantCard;